/**
 * Hook centralisé pour la gestion du niveau de fidélité client
 *
 * Récupère la configuration d'affichage du niveau de fidélité depuis le backend.
 * Le backend calcule automatiquement le statusLevel basé sur activityStats.
 *
 * IMPORTANTE : Ce hook NE CALCULE PAS le niveau de fidélité.
 * Le calcul est fait exclusivement côté backend (source de vérité unique).
 * Ce hook se contente de transformer la valeur backend en configuration d'affichage.
 *
 * @module hooks/useCustomerStatus
 * @author Équipe Brydge
 * @since 2.6.0
 */

import {useMemo} from 'react';
import {useStatusConfig} from './useStatusConfig';

/**
 * Récupère la configuration d'affichage du niveau de fidélité d'un client
 *
 * Le backend envoie customer.statusLevel (string: 'new', 'occasional', 'loyal',
 * 'privileged') calculé selon les seuils personnalisés de l'établissement. Ce
 * hook traduit ce niveau en apparence (libellé/icône/couleur) configurée par
 * l'établissement, avec repli sur les valeurs par défaut.
 *
 * @param {Object} customer - Objet client du backend
 * @param {string} customer.statusLevel - Niveau de fidélité calculé par le backend
 * @return {Object|null} Configuration du niveau (level, label, color, icon, tooltip) ou null
 *
 * @example
 * const statusConfig = useCustomerStatus(customer);
 * if (statusConfig) {
 *   return <Chip label={statusConfig.label} color={statusConfig.color} />;
 * }
 */
export function useCustomerStatus(customer) {
  const {getConfig} = useStatusConfig();

  return useMemo(() => {
    // Pas de repli : un client sans statut (tous désactivés sous son seuil) n'a aucun badge.
    return getConfig(customer?.statusLevel);
  }, [customer, getConfig]);
}
